import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useBroadcastConfig } from '@/api/hooks/useBroadcast'
import { useFbPageMedia, useIgMedia, useVideoInfo, useSaveVideoToLibrary, type MediaItem, type VideoInfo } from '@/api/hooks/useMediaDownload'
import { useMediaFolders, useCreateFolder } from '@/api/hooks/useMediaLibrary'
import { api, BASE, getAuthHeaders } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import {
  Download, Image, Video, Search, CheckSquare, Square, Loader2, Archive,
  ExternalLink, Camera, Film, AlertTriangle, Globe, Link2, Save, CheckCircle, Shield,
  Play, Clock, X, ArrowLeft, FolderOpen, Plus, Check, CalendarDays, Instagram, Facebook,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

type InputMode = 'connected' | 'url'
type MediaFilter = 'all' | 'photos' | 'videos'

interface ZipProgress {
  current: number
  total: number
  downloaded: number
  failed: number
  lastFile?: string
}

function isVideoUrl(url: string): boolean {
  // Threads and Instagram profile URLs — always scrape as profile
  if (/threads\.(net|com)\//i.test(url)) return false
  if (/instagram\.com\/[a-zA-Z0-9._]+\/?(\?|$)/i.test(url)) return false
  return /\/videos\/|\/reel\/|\/watch[/?]|fb\.watch\/|\/share\/[rv]\//i.test(url)
}

function extractPageName(url: string): string {
  try {
    const u = new URL(url)
    // profile.php?id=123 → "123"
    const id = u.searchParams.get('id')
    if (id) return id
    // facebook.com/PageName → "PageName"
    const path = u.pathname.replace(/^\//, '').split('/')[0]
    if (path && path !== 'profile.php') return decodeURIComponent(path)
  } catch {}
  return 'Facebook'
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatFilesize(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`
  return `${(bytes / 1024).toFixed(0)}KB`
}

export function MediaDownload() {
  const { t } = useLanguage()
  const { data: config, isLoading: configLoading } = useBroadcastConfig()
  const { data: cookiesStatus, refetch: refetchCookies } = useQuery<{ hasCookies: boolean; count: number }>({
    queryKey: ['media-download-cookies'],
    queryFn: () => api.get('/media-download/cookies'),
  })
  const [savingCookies, setSavingCookies] = useState(false)

  // Input mode
  const [inputMode, setInputMode] = useState<InputMode>('url')
  const [connectedPlatform, setConnectedPlatform] = useState<'facebook' | 'instagram'>('facebook')
  const [selectedPageId, setSelectedPageId] = useState('')
  const [selectedIgUserId, setSelectedIgUserId] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [cookiesInput, setCookiesInput] = useState('')
  const [showCookies, setShowCookies] = useState(false)
  const [scrapeNeedsLogin, setScrapeNeedsLogin] = useState(false)

  // Fullres upgrade progress
  const [fullresProgress, setFullresProgress] = useState<{ current: number; total: number } | null>(null)

  // Media state
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  // Scrape config
  const [scrapeType, setScrapeType] = useState<MediaFilter>('all')
  const [scrapeLimit, setScrapeLimit] = useState(150)

  // Scraping state
  const [isScraping, setIsScraping] = useState(false)
  const [scrapePhase, setScrapePhase] = useState('')
  const [scrapeFound, setScrapeFound] = useState({ photos: 0, videos: 0 })
  const [scrapedMedia, setScrapedMedia] = useState<{ photos: MediaItem[]; videos: MediaItem[] } | null>(null)
  const [scrapeElapsed, setScrapeElapsed] = useState(0)
  const scrapeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Date range filter
  const [useDateFilter, setUseDateFilter] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Video info state (fdownloader-style)
  const videoInfoMutation = useVideoInfo()
  const [videoInfoData, setVideoInfoData] = useState<VideoInfo | null>(null)
  const [videoInfoUrl, setVideoInfoUrl] = useState('')
  // Video quality dialog for grid items
  const [selectedGridVideo, setSelectedGridVideo] = useState<MediaItem | null>(null)
  const [gridVideoInfo, setGridVideoInfo] = useState<VideoInfo | null>(null)
  const [gridVideoLoading, setGridVideoLoading] = useState(false)

  // Save to library
  const { data: folders } = useMediaFolders()
  const createFolderMutation = useCreateFolder()
  const saveToLibraryMutation = useSaveVideoToLibrary()
  const [selectedFolderId, setSelectedFolderId] = useState<string>('')
  const [savingFormat, setSavingFormat] = useState<string | null>(null)
  const [savedResult, setSavedResult] = useState<{ folderName: string; filename: string; sizeMB: number } | null>(null)

  // Download progress
  const [downloadElapsed, setDownloadElapsed] = useState(0)
  const downloadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Anti-fingerprint
  const [antiFingerprint, setAntiFingerprint] = useState(false)
  const [testingFingerprint, setTestingFingerprint] = useState(false)
  const [fingerprintResult, setFingerprintResult] = useState<{
    original: { size: number; base64: string; aHash: string; dHash: string }
    processed: { size: number; base64: string; aHash: string; dHash: string }
    comparison: { aHashDistance: number; dHashDistance: number; aHashSimilarity: number; dHashSimilarity: number; rmWouldMatch: boolean; verdict: string }
  } | null>(null)

  // Auto-select first library folder
  const libraryFolders = useMemo(() => (folders || []).filter(f => f.type === 'library'), [folders])
  useEffect(() => {
    if (!selectedFolderId && libraryFolders.length > 0) {
      setSelectedFolderId(libraryFolders[0].id)
    }
  }, [libraryFolders, selectedFolderId])

  // ZIP state
  const [isGenerating, setIsGenerating] = useState(false)
  const [zipProgress, setZipProgress] = useState<ZipProgress | null>(null)
  const [downloadToken, setDownloadToken] = useState<string | null>(null)
  const [zipInfo, setZipInfo] = useState<{ totalFiles: number; failedCount: number; sizeMB: number } | null>(null)

  // Bulk save to library state
  const [isBulkSaving, setIsBulkSaving] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; saved: number; failed: number; lastFile?: string } | null>(null)
  const [bulkDone, setBulkDone] = useState<{ saved: number; failed: number; folder: string } | null>(null)
  const [bulkVideoQuality, setBulkVideoQuality] = useState<string>('sd')

  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  // Graph API media for connected pages (Facebook)
  const { data: apiFbMedia, isLoading: fbMediaLoading, refetch: refetchFbMedia } = useFbPageMedia(
    inputMode === 'connected' && connectedPlatform === 'facebook' ? selectedPageId : null,
    mediaFilter,
  )

  // Graph API media for connected accounts (Instagram)
  const { data: apiIgMedia, isLoading: igMediaLoading, refetch: refetchIgMedia } = useIgMedia(
    inputMode === 'connected' && connectedPlatform === 'instagram' ? selectedIgUserId : null,
    mediaFilter,
  )

  const apiMedia = connectedPlatform === 'instagram' ? apiIgMedia : apiFbMedia
  const mediaLoading = connectedPlatform === 'instagram' ? igMediaLoading : fbMediaLoading
  const refetchMedia = connectedPlatform === 'instagram' ? refetchIgMedia : refetchFbMedia

  // Current media items (from API or scraping)
  const allMedia = useMemo(() => {
    if (inputMode === 'connected' && apiMedia) {
      return [...apiMedia.photos, ...apiMedia.videos]
    }
    if (inputMode === 'url' && scrapedMedia) {
      return [...scrapedMedia.photos, ...scrapedMedia.videos]
    }
    return []
  }, [inputMode, apiMedia, scrapedMedia])

  // Filtered media
  const filteredMedia = useMemo(() => {
    let items = allMedia
    if (mediaFilter === 'photos') items = items.filter(m => m.type === 'photo')
    if (mediaFilter === 'videos') items = items.filter(m => m.type === 'video')
    if (search) {
      const q = search.toLowerCase()
      items = items.filter(m =>
        (m.name || m.title || '')?.toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q)
      )
    }
    return items
  }, [allMedia, mediaFilter, search])

  // Reset selections when media changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [allMedia])

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedIds(new Set(filteredMedia.slice(0, scrapeLimit).map(m => m.id)))
  const clearSelection = () => setSelectedIds(new Set())

  // Scrape media from URL
  const handleScrape = useCallback(async () => {
    if (!urlInput.trim()) { toast.error('Cole a URL da pagina do Facebook'); return }

    setIsScraping(true)
    setScrapePhase('starting')
    setScrapeFound({ photos: 0, videos: 0 })
    setScrapedMedia(null)
    setDownloadToken(null)
    setZipInfo(null)
    setScrapeNeedsLogin(false)
    setScrapeElapsed(0)
    scrapeTimerRef.current = setInterval(() => setScrapeElapsed(p => p + 1), 1000)

    const controller = new AbortController()
    abortRef.current = controller

    let cookies: unknown = undefined
    if (cookiesInput.trim()) {
      try { cookies = JSON.parse(cookiesInput.trim()) }
      catch { toast.error('Cookies invalidos - cole no formato JSON (EditThisCookie)'); setIsScraping(false); if (scrapeTimerRef.current) { clearInterval(scrapeTimerRef.current); scrapeTimerRef.current = null }; return }
    }

    try {
      const body: Record<string, unknown> = { url: urlInput.trim(), type: scrapeType, limit: scrapeLimit }
      if (cookies) body.cookies = cookies
      if (useDateFilter && dateFrom) body.date_from = dateFrom
      if (useDateFilter && dateTo) body.date_to = dateTo

      const resp = await fetch(`${BASE}/media-download/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: 'Erro no scraping' }))
        toast.error(err.error || 'Erro ao buscar midias')
        setIsScraping(false)
        if (scrapeTimerRef.current) { clearInterval(scrapeTimerRef.current); scrapeTimerRef.current = null }
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let eventType = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!mountedRef.current) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'progress') {
                setScrapePhase(data.phase || '')
                if (data.error && (data.error.includes('Login required') || data.error.includes('Login wall'))) setScrapeNeedsLogin(true)
                if (data.message && data.message.includes('Login wall')) setScrapeNeedsLogin(true)
                if (data.phase === 'warning' && data.message) setScrapeNeedsLogin(true)
                // Track fullres upgrade progress
                if (data.phase === 'fullres' && data.current !== undefined) {
                  setFullresProgress({ current: data.current, total: data.total })
                } else if (data.phase === 'fullres_done') {
                  setFullresProgress(null)
                }
                // Update found counts (timeline phase sends both photos+videos)
                if (data.phase === 'timeline' || data.phase === 'timeline_done') {
                  if (data.photos !== undefined || data.videos !== undefined) {
                    setScrapeFound({ photos: data.photos || 0, videos: data.videos || 0 })
                  }
                } else if (data.phase === 'downloading_photos' || data.phase === 'photos_downloaded') {
                  // Photo download phase — scraper is saving photos to disk
                } else if (data.found !== undefined) {
                  const isPhotoPhase = ['photos', 'photos_done', 'photos_hires', 'photos_end'].includes(data.phase)
                  setScrapeFound(prev => ({
                    ...prev,
                    [isPhotoPhase ? 'photos' : 'videos']: data.found,
                  }))
                }
              } else if (eventType === 'done') {
                setScrapedMedia({ photos: data.photos || [], videos: data.videos || [] })
                const msg = `Encontrado: ${data.totalPhotos || 0} fotos, ${data.totalVideos || 0} videos`
                if (!data.hasCookies && (data.totalPhotos || 0) <= 10) {
                  toast.info(msg + ' (limitado sem cookies)')
                  setScrapeNeedsLogin(true)
                } else {
                  toast.success(msg)
                }
              } else if (eventType === 'error') {
                toast.error(data.message || 'Erro no scraping')
              }
            } catch {}
            eventType = ''
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') toast.error('Erro na conexao com o servidor')
    } finally {
      abortRef.current = null
      if (scrapeTimerRef.current) { clearInterval(scrapeTimerRef.current); scrapeTimerRef.current = null }
      if (mountedRef.current) setIsScraping(false)
    }
  }, [urlInput, scrapeType, scrapeLimit, cookiesInput, useDateFilter, dateFrom, dateTo])

  // Handle URL submission — detect video vs profile/page
  const handleUrlSubmit = useCallback(async () => {
    const url = urlInput.trim()
    if (!url) { toast.error('Cole a URL do Facebook'); return }

    if (isVideoUrl(url)) {
      // Single video — fdownloader flow
      setVideoInfoData(null)
      setVideoInfoUrl(url)
      setScrapedMedia(null)
      setDownloadToken(null)
      setZipInfo(null)
      setSavedResult(null)
      try {
        const data = await videoInfoMutation.mutateAsync(url)
        if (mountedRef.current) setVideoInfoData(data)
      } catch (e) {
        toast.error((e as Error).message || 'Erro ao buscar info do video')
      }
    } else {
      // Profile/page — scraping flow
      setVideoInfoData(null)
      setVideoInfoUrl('')
      handleScrape()
    }
  }, [urlInput, scrapeType, scrapeLimit, handleScrape])

  // Save video to library (R2)
  const handleSaveToLibrary = useCallback(async (url: string, formatId: string, needsMerge: boolean, title?: string, directUrl?: string) => {
    if (!selectedFolderId) {
      toast.error('Selecione uma pasta da biblioteca')
      return
    }
    setSavingFormat(formatId)
    setSavedResult(null)
    setDownloadElapsed(0)
    downloadTimerRef.current = setInterval(() => {
      setDownloadElapsed(prev => prev + 1)
    }, 1000)
    try {
      const result = await saveToLibraryMutation.mutateAsync({
        url, formatId, needsMerge, folderId: selectedFolderId, title, directUrl,
      })
      setSavedResult({
        folderName: result.folder.name,
        filename: result.image.filename,
        sizeMB: result.sizeMB,
      })
      toast.success(`Salvo na pasta "${result.folder.name}" (${result.sizeMB}MB)`)
    } catch (e) {
      toast.error((e as Error).message || 'Erro ao salvar na biblioteca')
    } finally {
      if (downloadTimerRef.current) clearInterval(downloadTimerRef.current)
      downloadTimerRef.current = null
      if (mountedRef.current) {
        setSavingFormat(null)
        setDownloadElapsed(0)
      }
    }
  }, [selectedFolderId, saveToLibraryMutation])

  // Open quality picker for a grid video
  const handleGridVideoClick = useCallback(async (item: MediaItem) => {
    if (item.type !== 'video' || !item.url) return
    setSelectedGridVideo(item)
    setGridVideoInfo(null)
    setGridVideoLoading(true)
    setSavedResult(null)
    try {
      const data = await videoInfoMutation.mutateAsync(item.url)
      if (mountedRef.current) setGridVideoInfo(data)
    } catch (e) {
      toast.error((e as Error).message || 'Erro ao buscar qualidades')
      setSelectedGridVideo(null)
    } finally {
      if (mountedRef.current) setGridVideoLoading(false)
    }
  }, [])

  // Generate ZIP (existing)
  const handleGenerateZip = useCallback(async () => {
    const selected = filteredMedia.filter(m => selectedIds.has(m.id))
    if (selected.length === 0) { toast.error('Selecione pelo menos 1 midia'); return }
    if (selected.length > 500) { toast.error('Maximo de 500 midias por vez'); return }

    setIsGenerating(true)
    setZipProgress(null)
    setDownloadToken(null)
    setZipInfo(null)

    const controller = new AbortController()
    abortRef.current = controller

    const items = selected.map((m, i) => ({
      url: m.url,
      type: m.type,
      filename: m.type === 'video' ? `video_${i + 1}.mp4` : `foto_${i + 1}.jpg`,
    }))

    try {
      const resp = await fetch(`${BASE}/media-download/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ items, antiFingerprint }),
        signal: controller.signal,
      })

      if (!resp.ok || !resp.body) { toast.error('Erro ao gerar ZIP'); setIsGenerating(false); return }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let eventType = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!mountedRef.current) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'progress') setZipProgress(data)
              else if (eventType === 'done') {
                setDownloadToken(data.token)
                setZipInfo({ totalFiles: data.totalFiles, failedCount: data.failedCount, sizeMB: data.sizeMB })
                toast.success(`ZIP pronto! ${data.totalFiles} arquivos (${data.sizeMB}MB)`)
              } else if (eventType === 'error') {
                toast.error(data.message || 'Erro ao gerar ZIP')
              }
            } catch {}
            eventType = ''
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') toast.error('Erro na conexao com o servidor')
    } finally {
      abortRef.current = null
      if (mountedRef.current) setIsGenerating(false)
    }
  }, [filteredMedia, selectedIds, antiFingerprint])

  // Bulk save to library (R2)
  const handleBulkSave = useCallback(async () => {
    const selected = filteredMedia.filter(m => selectedIds.has(m.id))
    if (selected.length === 0) { toast.error('Selecione pelo menos 1 midia'); return }
    if (selected.length > 500) { toast.error('Maximo de 500 midias por vez'); return }

    setIsBulkSaving(true)
    setBulkProgress(null)
    setBulkDone(null)

    const controller = new AbortController()
    abortRef.current = controller

    const items = selected.map((m, i) => ({
      url: m.type === 'video' && m.permalink ? m.permalink : m.url,
      type: m.type,
      filename: m.type === 'video' ? `video_${i + 1}` : `foto_${i + 1}`,
      ...(m.localPath ? { localPath: m.localPath } : {}),
      ...(m.type === 'video' && m.url ? { thumbnail_url: m.url } : {}),
      ...(m.permalink ? { permalink: m.permalink } : {}),
    }))

    // Extract page name from URL for auto-folder naming
    const pageName = extractPageName(urlInput || '')

    try {
      const resp = await fetch(`${BASE}/media-download/bulk-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ items, page_name: pageName, video_quality: bulkVideoQuality }),
        signal: controller.signal,
      })

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: 'Erro' }))
        toast.error(err.error || 'Erro ao salvar na biblioteca')
        setIsBulkSaving(false)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let eventType = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!mountedRef.current) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'progress') setBulkProgress(data)
              else if (eventType === 'done') {
                setBulkDone(data)
                toast.success(`${data.saved} arquivos salvos na pasta "${data.folder}"`)
              } else if (eventType === 'error') {
                toast.error(data.message || 'Erro ao salvar')
              }
            } catch {}
            eventType = ''
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') toast.error('Erro na conexao com o servidor')
    } finally {
      abortRef.current = null
      if (mountedRef.current) setIsBulkSaving(false)
    }
  }, [filteredMedia, selectedIds, urlInput, bulkVideoQuality])

  const handleDownload = () => {
    if (!downloadToken) return
    window.open(`${BASE}/media-download/download/${downloadToken}`, '_blank')
  }

  const handleFetchConnected = () => {
    if (connectedPlatform === 'instagram') {
      if (!selectedIgUserId) { toast.error('Selecione uma conta'); return }
    } else {
      if (!selectedPageId) { toast.error('Selecione uma pagina'); return }
    }
    setScrapedMedia(null)
    setDownloadToken(null)
    setZipInfo(null)
    setVideoInfoData(null)
    refetchMedia()
  }

  const allAccounts = config?.accounts || []
  const accounts = connectedPlatform === 'instagram'
    ? allAccounts.filter((a: any) => !a.fbOnly && a.igUserId)
    : allAccounts
  const hasMedia = allMedia.length > 0
  const photoCount = inputMode === 'connected' ? (apiMedia?.totalPhotos || 0) : (scrapedMedia?.photos.length || 0)
  const videoCount = inputMode === 'connected' ? (apiMedia?.totalVideos || 0) : (scrapedMedia?.videos.length || 0)

  if (configLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  // ─── Quality Picker Card (reused for both flows) ────────────────────
  const QualityCard = ({ info, videoUrl, onClose }: { info: VideoInfo; videoUrl: string; onClose?: () => void }) => (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div className="flex items-start gap-4">
        {/* Thumbnail */}
        <div className="relative w-48 shrink-0 overflow-hidden rounded-lg bg-muted">
          {info.thumbnail ? (
            <img src={info.thumbnail} alt={info.title} className="w-full aspect-video object-cover" />
          ) : (
            <div className="flex aspect-video items-center justify-center text-muted-foreground/30">
              <Play className="h-10 w-10" />
            </div>
          )}
          {info.duration && (
            <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
              <Clock className="h-2.5 w-2.5" />
              {formatDuration(info.duration)}
            </div>
          )}
        </div>

        {/* Info + buttons */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium leading-tight line-clamp-2">{info.title}</h3>
            {onClose && (
              <button onClick={onClose} className="shrink-0 rounded p-1 hover:bg-muted">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>

          <div className="space-y-2">
            {savingFormat ? (
              /* Save in progress */
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
                  <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {t('Salvando na biblioteca...')} ({downloadElapsed}s)
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {downloadElapsed < 5 && t('Baixando video do Facebook...')}
                      {downloadElapsed >= 5 && downloadElapsed < 15 && t('Baixando audio e video separados...')}
                      {downloadElapsed >= 15 && downloadElapsed < 30 && t('Convertendo para formato compativel (H.264)...')}
                      {downloadElapsed >= 30 && downloadElapsed < 60 && t('Enviando para a nuvem (R2)...')}
                      {downloadElapsed >= 60 && t('Video grande, ainda processando...')}
                    </p>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{savingFormat}</span>
                </div>
              </div>
            ) : savedResult ? (
              /* Save success */
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30 px-4 py-3">
                  <Check className="h-5 w-5 text-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      {t('Salvo na biblioteca!')}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {savedResult.filename} ({savedResult.sizeMB}MB) → {t('pasta')} "{savedResult.folderName}"
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs h-7"
                  onClick={() => setSavedResult(null)}
                >
                  {t('Salvar outro formato')}
                </Button>
              </div>
            ) : (
              /* Quality selection + folder picker */
              <>
                {/* Folder selector */}
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
                    <SelectTrigger className="h-8 flex-1 text-xs">
                      <SelectValue placeholder={t('Selecione uma pasta...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {libraryFolders.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.parentName ? `${f.parentName} / ${f.name}` : f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 shrink-0"
                    title={t('Criar pasta')}
                    onClick={async () => {
                      const name = prompt(t('Nome da nova pasta:'))
                      if (!name?.trim()) return
                      try {
                        const res = await createFolderMutation.mutateAsync({ name: name.trim() })
                        setSelectedFolderId(res.folder.id)
                        toast.success(`Pasta "${name.trim()}" criada`)
                      } catch { toast.error('Erro ao criar pasta') }
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">{t('Salvar na biblioteca (nuvem) — nao ocupa espaco no seu PC')}:</p>
                <div className="flex flex-wrap gap-2">
                  {info.formats.map(f => (
                    <Button
                      key={f.format_id}
                      size="sm"
                      variant={f.label === 'HD' || f.label === '1080p' ? 'default' : 'outline'}
                      className="gap-1.5 text-xs h-8"
                      disabled={!selectedFolderId}
                      onClick={() => handleSaveToLibrary(videoUrl, f.format_id, f.needsMerge, info.title, f.directUrl)}
                    >
                      <Save className="h-3 w-3" />
                      {f.label}
                      {f.filesize && <span className="text-muted-foreground">({formatFilesize(f.filesize)})</span>}
                    </Button>
                  ))}
                </div>
                {info.formats.some(f => f.needsMerge) && (
                  <p className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {t('Qualidades acima de HD processam video+audio separados (~30s)')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <Download className="h-5 w-5" />
            {t('Download de Midias')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('Baixe fotos e videos do Facebook e Instagram')}
          </p>
        </div>
        {hasMedia && (
          <Badge variant="outline" className="text-xs">
            {photoCount} {t('fotos')} / {videoCount} {t('videos')}
          </Badge>
        )}
      </div>

      {/* Input Mode */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex gap-2">
          <Button
            variant={inputMode === 'url' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setInputMode('url'); setDownloadToken(null) }}
            className="gap-2"
          >
            <Link2 className="h-3.5 w-3.5" />
            {t('Por URL')}
          </Button>
          <Button
            variant={inputMode === 'connected' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setInputMode('connected'); setScrapedMedia(null); setVideoInfoData(null); setDownloadToken(null) }}
            className="gap-2"
          >
            <Globe className="h-3.5 w-3.5" />
            {t('Paginas conectadas')}
          </Button>
        </div>

        {inputMode === 'connected' ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={connectedPlatform === 'facebook' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setConnectedPlatform('facebook'); setSelectedIgUserId('') }}
                className="gap-1.5"
              >
                <Facebook className="h-3.5 w-3.5" />
                Facebook
              </Button>
              <Button
                variant={connectedPlatform === 'instagram' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setConnectedPlatform('instagram'); setSelectedPageId('') }}
                className="gap-1.5"
              >
                <Instagram className="h-3.5 w-3.5" />
                Instagram
              </Button>
            </div>
            <div className="flex items-center gap-3">
              {connectedPlatform === 'instagram' ? (
                <Select value={selectedIgUserId} onValueChange={setSelectedIgUserId}>
                  <SelectTrigger className="h-9 flex-1 text-sm">
                    <SelectValue placeholder={t('Selecione uma conta do Instagram...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a: any) => (
                      <SelectItem key={a.igUserId} value={a.igUserId}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                  <SelectTrigger className="h-9 flex-1 text-sm">
                    <SelectValue placeholder={t('Selecione uma pagina...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a: { pageId: string; name: string }) => (
                      <SelectItem key={a.pageId} value={a.pageId}>
                        {a.name} ({a.pageId})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                onClick={handleFetchConnected}
                disabled={(connectedPlatform === 'instagram' ? !selectedIgUserId : !selectedPageId) || mediaLoading}
                className="gap-2"
                size="sm"
              >
                {mediaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {t('Buscar Midias')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Input
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleUrlSubmit()}
                placeholder="facebook.com/Pagina, instagram.com/usuario, threads.net/@usuario"
                className="h-9 flex-1 text-sm"
              />
              <Button
                onClick={handleUrlSubmit}
                disabled={isScraping || videoInfoMutation.isPending || !urlInput.trim()}
                className="gap-2"
                size="sm"
              >
                {(isScraping || videoInfoMutation.isPending) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {isScraping ? t('Buscando...') : t('Buscar')}
              </Button>
            </div>

            {/* URL type hint */}
            <p className="text-[11px] text-muted-foreground/60">
              {t('Cole a URL de um video/reel, pagina do Facebook, perfil do Instagram ou Threads.')}
            </p>

            {/* Scrape config: type + limit */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">{t('Tipo')}:</span>
                <div className="flex gap-1">
                  <Button variant={scrapeType === 'all' ? 'default' : 'outline'} size="sm" className="h-6 gap-1 px-2 text-[10px]" onClick={() => setScrapeType('all')}>
                    {t('Tudo')}
                  </Button>
                  <Button variant={scrapeType === 'photos' ? 'default' : 'outline'} size="sm" className="h-6 gap-1 px-2 text-[10px]" onClick={() => setScrapeType('photos')}>
                    <Camera className="h-2.5 w-2.5" /> {t('Fotos')}
                  </Button>
                  <Button variant={scrapeType === 'videos' ? 'default' : 'outline'} size="sm" className="h-6 gap-1 px-2 text-[10px]" onClick={() => setScrapeType('videos')}>
                    <Film className="h-2.5 w-2.5" /> {t('Reels/Videos')}
                  </Button>
                </div>
              </div>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">{t('Limite')}:</span>
                <Select value={String(scrapeLimit)} onValueChange={(v) => setScrapeLimit(Number(v))}>
                  <SelectTrigger className="h-6 w-20 text-[10px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="150">150</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="300">300</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date range filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={useDateFilter} onCheckedChange={(v) => setUseDateFilter(!!v)} />
                <span className="text-xs font-medium flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                  {t('Filtrar por periodo')}
                </span>
              </label>
              {useDateFilter && (
                <div className="flex items-center gap-2 pl-6">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    className="h-8 w-40 text-xs"
                    placeholder="Data inicial"
                  />
                  <span className="text-xs text-muted-foreground">{t('ate')}</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    className="h-8 w-40 text-xs"
                    placeholder="Data final"
                  />
                  <p className="text-[10px] text-muted-foreground/60 ml-2">
                    {t('Varre a timeline do perfil por data (mais lento)')}
                  </p>
                </div>
              )}
            </div>

            {/* Cookies */}
            <div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCookies(!showCookies)} className="text-[11px] text-primary hover:underline">
                  {showCookies ? t('Ocultar cookies') : t('Cookies do Facebook')}
                </button>
                {cookiesStatus?.hasCookies && (
                  <Badge variant="outline" className="gap-1 text-[9px] text-emerald-600 border-emerald-500/30">
                    <CheckCircle className="h-2.5 w-2.5" /> {t('Cookies salvos')} ({cookiesStatus.count})
                  </Badge>
                )}
              </div>
              {showCookies && (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={cookiesInput}
                    onChange={e => setCookiesInput(e.target.value)}
                    placeholder={t('Cole aqui os cookies do Facebook (formato JSON do EditThisCookie)...')}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono h-20 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs h-7"
                      disabled={!cookiesInput.trim() || savingCookies}
                      onClick={async () => {
                        try {
                          const parsed = JSON.parse(cookiesInput.trim())
                          setSavingCookies(true)
                          await api.post('/media-download/cookies', { cookies: parsed })
                          toast.success(t('Cookies salvos no servidor!'))
                          refetchCookies()
                          setCookiesInput('')
                        } catch { toast.error(t('Cookies invalidos - cole no formato JSON')) }
                        finally { setSavingCookies(false) }
                      }}
                    >
                      <Save className="h-3 w-3" /> {t('Salvar no servidor')}
                    </Button>
                    {cookiesStatus?.hasCookies && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="gap-1 text-xs h-7"
                        onClick={async () => {
                          try {
                            await api.post('/media-download/cookies', { cookies: [] })
                            toast.success(t('Cookies removidos'))
                            refetchCookies()
                          } catch { toast.error(t('Erro ao remover cookies')) }
                        }}
                      >
                        <X className="h-3 w-3" /> {t('Limpar cookies')}
                      </Button>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {t('Cookies salvos sao usados automaticamente')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Login required warning */}
            {scrapeNeedsLogin && !isScraping && (
              <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
                <div>
                  <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400">
                    {t('Facebook exige login para acessar esta pagina')}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {t('Instale a extensao EditThisCookie no Chrome, acesse o Facebook logado, exporte os cookies e cole acima.')}
                  </p>
                </div>
              </div>
            )}

            {/* Scraping progress */}
            {isScraping && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm font-medium">
                      {scrapePhase === 'starting' && t('Iniciando navegador...')}
                      {scrapePhase === 'cookies' && t('Carregando cookies...')}
                      {scrapePhase === 'warning' && t('Conectando ao Facebook...')}
                      {(scrapePhase === 'timeline') && t('Varrendo timeline por periodo...')}
                      {scrapePhase === 'timeline_done' && t('Timeline concluida!')}
                      {(scrapePhase === 'photos' || scrapePhase === 'scanning_photos') && t('Varrendo fotos do perfil...')}
                      {scrapePhase === 'photos_done' && t('Fotos concluidas!')}
                      {scrapePhase === 'photos_end' && t('Scroll concluido, preparando full resolution...')}
                      {scrapePhase === 'photos_hires' && t('Melhorando qualidade das fotos...')}
                      {(scrapePhase === 'fullres' || scrapePhase === 'fullres_done') && (
                        fullresProgress
                          ? `${t('Capturando full resolution...')} ${fullresProgress.current}/${fullresProgress.total}`
                          : t('Full resolution concluido!')
                      )}
                      {(scrapePhase === 'timeline_fullres') && (
                        fullresProgress
                          ? `${t('Capturando full resolution...')} ${fullresProgress.current}/${fullresProgress.total}`
                          : t('Capturando full resolution...')
                      )}
                      {(scrapePhase === 'videos' || scrapePhase === 'scanning_videos') && t('Varrendo videos do perfil...')}
                      {scrapePhase === 'videos_done' && t('Videos concluidos!')}
                      {scrapePhase === 'downloading_photos' && t('Baixando fotos para cache...')}
                      {scrapePhase === 'photos_downloaded' && t('Fotos em cache!')}
                    </span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{scrapeElapsed}s</span>
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  {scrapeFound.photos > 0 && (
                    <span className="flex items-center gap-1">
                      <Camera className="h-3 w-3" /> {scrapeFound.photos} {t('fotos')}
                    </span>
                  )}
                  {scrapeFound.videos > 0 && (
                    <span className="flex items-center gap-1">
                      <Film className="h-3 w-3" /> {scrapeFound.videos} {t('videos')}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground/60">
                  {(scrapePhase === 'fullres' || scrapePhase === 'timeline_fullres')
                    ? t('Abrindo cada foto para capturar em resolução máxima. ~3s por foto.')
                    : t('Scroll infinito — varrendo todo o perfil. Pode levar alguns minutos para perfis grandes.')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Video Info Card (fdownloader-style, single video) ──── */}
      {videoInfoData && videoInfoUrl && (
        <QualityCard
          info={videoInfoData}
          videoUrl={videoInfoUrl}
          onClose={() => { setVideoInfoData(null); setVideoInfoUrl('') }}
        />
      )}

      {/* Loading video info */}
      {videoInfoMutation.isPending && !videoInfoData && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">{t('Extraindo informacoes do video...')}</span>
          </div>
        </div>
      )}

      {/* ─── Grid Video Quality Dialog ──── */}
      {selectedGridVideo && (
        <div className="rounded-xl border-2 border-primary/30 bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectedGridVideo(null); setGridVideoInfo(null) }}
              className="rounded p-1 hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <h3 className="text-sm font-medium">{t('Escolha a qualidade')}</h3>
          </div>
          {gridVideoLoading ? (
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">{t('Extraindo qualidades disponiveis...')}</span>
            </div>
          ) : gridVideoInfo ? (
            <QualityCard
              info={gridVideoInfo}
              videoUrl={selectedGridVideo.url}
              onClose={() => { setSelectedGridVideo(null); setGridVideoInfo(null) }}
            />
          ) : null}
        </div>
      )}

      {/* Media Filter + Controls */}
      {hasMedia && !selectedGridVideo && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
          <div className="flex gap-1">
            <Button variant={mediaFilter === 'all' ? 'default' : 'outline'} size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setMediaFilter('all')}>
              {t('Todas')} ({allMedia.length})
            </Button>
            <Button variant={mediaFilter === 'photos' ? 'default' : 'outline'} size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setMediaFilter('photos')}>
              <Camera className="h-3 w-3" /> {t('Fotos')} ({photoCount})
            </Button>
            <Button variant={mediaFilter === 'videos' ? 'default' : 'outline'} size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setMediaFilter('videos')}>
              <Film className="h-3 w-3" /> {t('Videos')} ({videoCount})
            </Button>
          </div>

          <div className="flex-1" />

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Buscar...')} className="h-7 w-40 pl-7 text-xs" />
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={antiFingerprint} onCheckedChange={(v) => setAntiFingerprint(!!v)} />
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Shield className="h-3 w-3" /> Anti-RM
              </span>
            </label>
            {selectedIds.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 text-[10px] px-2"
                disabled={testingFingerprint}
                onClick={async () => {
                  const firstSelected = filteredMedia.find(m => selectedIds.has(m.id) && m.type === 'photo')
                  if (!firstSelected) { toast.error('Selecione pelo menos 1 foto'); return }
                  setTestingFingerprint(true)
                  setFingerprintResult(null)
                  try {
                    const res = await fetch(`${BASE}/media-download/test-anti-fingerprint`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                      body: JSON.stringify({ url: firstSelected.url || firstSelected.thumbnail }),
                    })
                    const data = await res.json()
                    if (data.error) { toast.error(data.error); return }
                    setFingerprintResult(data)
                  } catch { toast.error('Erro ao testar') } finally { setTestingFingerprint(false) }
                }}
              >
                {testingFingerprint ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                {t('Testar')}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={selectAll}>
              <CheckSquare className="h-3 w-3" /> {t('Tudo')}
            </Button>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={clearSelection}>
              <Square className="h-3 w-3" /> {t('Limpar')}
            </Button>
            {selectedIds.size > 0 && (
              <Badge className="text-xs">{selectedIds.size} {t('selecionados')}</Badge>
            )}
          </div>
        </div>
      )}

      {/* Bulk Save Progress */}
      {isBulkSaving && bulkProgress && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            {t('Salvando na biblioteca...')} ({bulkProgress.current}/{bulkProgress.total})
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%` }} />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{bulkProgress.saved} {t('salvos')} / {bulkProgress.failed} {t('falharam')}</span>
            <span>{bulkProgress.lastFile}</span>
          </div>
          <p className="text-[10px] text-muted-foreground/60">
            {bulkVideoQuality !== 'sd'
              ? `${t('Videos em')} ${bulkVideoQuality.toUpperCase()} — ${t('cada video pode levar 30-120s')}`
              : t('Videos em SD para maior velocidade')}
          </p>
        </div>
      )}

      {/* Bulk Save Done */}
      {bulkDone && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  {bulkDone.saved} {t('arquivos salvos na pasta')} "{bulkDone.folder}"
                </p>
                {bulkDone.failed > 0 && (
                  <p className="flex items-center gap-1 text-xs text-yellow-600">
                    <AlertTriangle className="h-3 w-3" />
                    {bulkDone.failed} {t('falharam')}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setBulkDone(null)} className="gap-1.5 text-xs">
              {t('Fechar')}
            </Button>
          </div>
        </div>
      )}

      {/* Anti-fingerprint test result */}
      {fingerprintResult && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" /> {t('Teste Anti-fingerprint')}
            </h3>
            <button onClick={() => setFingerprintResult(null)} className="text-muted-foreground hover:text-foreground">
              <span className="text-xs">Fechar</span>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs font-medium text-center text-muted-foreground">{t('Original')}</p>
              <img src={fingerprintResult.original.base64} alt="Original" className="w-full rounded-lg border border-border" />
              <div className="space-y-1 text-[10px] font-mono text-muted-foreground">
                <p>aHash: <span className="text-foreground">{fingerprintResult.original.aHash}</span></p>
                <p>dHash: <span className="text-foreground">{fingerprintResult.original.dHash}</span></p>
                <p>{t('Tamanho')}: {Math.round(fingerprintResult.original.size / 1024)}KB</p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-center text-muted-foreground">{t('Processada')}</p>
              <img src={fingerprintResult.processed.base64} alt="Processed" className="w-full rounded-lg border border-border" />
              <div className="space-y-1 text-[10px] font-mono text-muted-foreground">
                <p>aHash: <span className="text-foreground">{fingerprintResult.processed.aHash}</span></p>
                <p>dHash: <span className="text-foreground">{fingerprintResult.processed.dHash}</span></p>
                <p>{t('Tamanho')}: {Math.round(fingerprintResult.processed.size / 1024)}KB</p>
              </div>
            </div>
          </div>
          <div className={`rounded-lg p-3 text-center ${fingerprintResult.comparison.rmWouldMatch ? 'border border-red-500/30 bg-red-500/5' : 'border border-emerald-500/30 bg-emerald-500/5'}`}>
            <p className={`text-sm font-medium ${fingerprintResult.comparison.rmWouldMatch ? 'text-red-600' : 'text-emerald-600'}`}>
              {fingerprintResult.comparison.verdict}
            </p>
            <div className="mt-2 flex justify-center gap-6 text-xs text-muted-foreground">
              <span>aHash: {fingerprintResult.comparison.aHashDistance}/64 bits diferentes ({fingerprintResult.comparison.aHashSimilarity}% similar)</span>
              <span>dHash: {fingerprintResult.comparison.dHashDistance}/64 bits diferentes ({fingerprintResult.comparison.dHashSimilarity}% similar)</span>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Facebook RM detecta com {'<'}10 bits de diferenca. Quanto mais bits diferentes, mais seguro.
            </p>
          </div>
        </div>
      )}

      {/* Media Grid */}
      {hasMedia && !selectedGridVideo && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filteredMedia.map(item => {
            const isSelected = selectedIds.has(item.id)
            return (
              <div
                key={item.id}
                className={`group relative cursor-pointer overflow-hidden rounded-lg border-2 transition-all ${
                  isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
                }`}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-muted" onClick={() => toggleSelect(item.id)}>
                  {item.thumbnail ? (
                    <img src={item.thumbnail} alt={item.name || item.title || item.id} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground/30">
                      {item.type === 'video' ? <Video className="h-8 w-8" /> : <Image className="h-8 w-8" />}
                    </div>
                  )}
                </div>

                {/* Checkbox overlay */}
                <div className="absolute left-2 top-2" onClick={() => toggleSelect(item.id)}>
                  <Checkbox
                    checked={isSelected}
                    className="h-5 w-5 border-2 border-white bg-black/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                </div>

                {/* Type badge */}
                <div className="absolute right-2 top-2">
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${
                      item.type === 'video' ? 'border-red-500/50 bg-red-500/80 text-white' : 'border-blue-500/50 bg-blue-500/80 text-white'
                    }`}
                  >
                    {item.type === 'video' ? <Film className="mr-1 h-2.5 w-2.5" /> : <Camera className="mr-1 h-2.5 w-2.5" />}
                    {item.type === 'video' ? 'Video' : 'Foto'}
                  </Badge>
                </div>

                {/* Bottom overlay with actions */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      {(item.name || item.title) && (
                        <p className="truncate text-[10px] text-white">{item.name || item.title}</p>
                      )}
                      {item.created_time && (
                        <p className="text-[9px] text-white/60">{new Date(item.created_time).toLocaleDateString('pt-BR')}</p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {/* Download quality button for videos */}
                      {item.type === 'video' && item.url && (
                        <button
                          onClick={e => { e.stopPropagation(); handleGridVideoClick(item) }}
                          className="rounded-full bg-primary/80 p-1.5 hover:bg-primary"
                          title="Download com qualidade"
                        >
                          <Download className="h-3 w-3 text-white" />
                        </button>
                      )}
                      {/* Open in FB */}
                      {item.url && (
                        <button
                          onClick={e => { e.stopPropagation(); window.open(item.url, '_blank') }}
                          className="rounded-full bg-black/40 p-1.5 hover:bg-black/60"
                          title="Abrir no Facebook"
                        >
                          <ExternalLink className="h-3 w-3 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Empty state */}
      {!hasMedia && !mediaLoading && !isScraping && !videoInfoData && !videoInfoMutation.isPending && (
        <div className="rounded-xl border border-dashed border-border py-20 text-center">
          <Download className="mx-auto mb-3 h-10 w-10 text-muted-foreground/20" />
          <p className="text-sm text-muted-foreground">{t('Cole uma URL de video ou pagina do Facebook')}</p>
          <p className="mt-1 text-xs text-muted-foreground/50">{t('Videos/Reels = download direto | Paginas/Perfis = lista todos os videos')}</p>
        </div>
      )}

      {/* Loading */}
      {mediaLoading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      )}

      {/* Bulk action buttons */}
      {hasMedia && selectedIds.size > 0 && !isGenerating && !isBulkSaving && !downloadToken && !bulkDone && !selectedGridVideo && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-col items-center gap-2">
            {/* Video quality selector */}
            {videoCount > 0 && (
              <div className="flex items-center gap-2 rounded-lg bg-card/95 backdrop-blur border border-border px-3 py-1.5 shadow-lg">
                <Film className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{t('Qualidade dos videos')}:</span>
                {(['sd', 'hd', '720p', '1080p'] as const).map(q => (
                  <button
                    key={q}
                    onClick={() => setBulkVideoQuality(q)}
                    className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                      bulkVideoQuality === q
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {q.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button onClick={handleBulkSave} size="lg" className="gap-2 shadow-lg">
                <Save className="h-4 w-4" />
                {t('Salvar na Biblioteca')} ({selectedIds.size})
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/50">
              {bulkVideoQuality !== 'sd' && videoCount > 0
                ? t('Videos em ') + bulkVideoQuality.toUpperCase() + t(' — mais lento, cada video ~30-120s')
                : t('Salva direto na biblioteca com pastas Fotos/Reels automaticamente')}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
